/**
 * Mass Master Pro - Project Estimate
 * User-friendly estimate interface with dropdown menus for labor/equipment
 * Inspired by Buildxact, PlanSwift, and Procore
 * Supports 6 professional templates with company branding
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FileText, Download, Mail, Save, Calculator, DollarSign, Percent,
  Building2, Truck, Shovel, Package, Plus, Trash2, Users, HardHat,
  ChevronDown, ChevronUp, Eye, EyeOff, Leaf, Construction, Send,
  Copy, CheckCircle, AlertCircle, Clock, Layout, FileSpreadsheet,
  Palette, MapPin, Phone, Globe, Award, Settings2, Layers, PackageOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { toast } from 'sonner';
import { defaultLaborTypes, defaultEquipmentTypes } from './LaborEquipmentRates';
import { 
  downloadPDFReport, 
  downloadWordDocument, 
  downloadComprehensiveExcel,
  getPDFBase64,
  copyReportToClipboard
} from '../utils/exportUtils';
import SavedEstimates from './SavedEstimates';

// Template definitions
const ESTIMATE_TEMPLATES = [
  { id: 'professional', name: 'Professional', description: 'Clean, corporate look with blue accents', icon: FileText, color: 'blue' },
  { id: 'modern', name: 'Modern', description: 'Sleek dark theme with subtle gradients', icon: Palette, color: 'slate' },
  { id: 'classic', name: 'Classic', description: 'Traditional estimate format', icon: Layout, color: 'gray' },
  { id: 'minimal', name: 'Minimal', description: 'Simple, clean layout with focus on data', icon: FileText, color: 'slate' },
  { id: 'excel', name: 'Excel Style', description: 'Spreadsheet-like appearance with grid lines', icon: FileSpreadsheet, color: 'green' },
  { id: 'contractor', name: 'Contractor Pro', description: 'Bold design for construction professionals', icon: HardHat, color: 'orange' },
];

// Format helpers
const formatCurrency = (num) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num || 0);
};

const EstimateSpreadsheet = ({
  // Calculator data
  areaMaterials = [],
  bulkMaterials = [],
  excavationMaterials = [],
  pipingMaterials = [],
  tankMaterials = [],
  buildings = [],
  backfillItems = [],
  truckingCalculations = [],
  customImportVolumes = [],
  customExcavationVolumes = [],
  civilItems = [],
  environmentalItems = [],
  mobilizationData = {},
  // Totals
  areaTotals = {},
  bulkTotals = {},
  excavationTotals = {},
  backfillTotals = {},
  pipingTotals = {},
  tanksTotals = {},
  buildingsTotals = {},
  customImportTotals = {},
  customExcavationTotals = {},
  // Calculator-specific labor & equipment
  calculatorLaborEquipment = {},
  // Task Durations
  taskDurations = {},
  // Old Mobilization & Demobilization (deprecated)
  mobilization: externalMobilization,
  setMobilization: setExternalMobilization,
}) => {
  // Project Info
  const [projectInfo, setProjectInfo] = useState(() => {
    const saved = localStorage.getItem('estimateProjectInfo');
    return saved ? JSON.parse(saved) : {
      name: '',
      number: '',
      date: new Date().toISOString().split('T')[0],
      location: '',
      client: '',
      clientEmail: '',
    };
  });

  // Company Info (for estimate header)
  const [companyInfo, setCompanyInfo] = useState(() => {
    const saved = localStorage.getItem('estimateCompanyInfo');
    return saved ? JSON.parse(saved) : {
      name: '',
      address: '',
      city: '',
      phone: '',
      email: '',
      website: '',
      license: '',
      logo: null,
    };
  });

  // Template selection (6 templates)
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    const saved = localStorage.getItem('estimateTemplate');
    return saved || 'professional';
  });

  // Mobilization & Demobilization (internal state if not passed from parent)
  const [internalMobilization, setInternalMobilization] = useState(() => {
    const saved = localStorage.getItem('estimateMobilization');
    return saved ? JSON.parse(saved) : {
      mobilizationCost: 0,
      demobilizationCost: 0,
      mobilizationDescription: 'Site mobilization, setup, and preparation',
      demobilizationDescription: 'Site cleanup, equipment removal, and demobilization',
    };
  });

  // Use external mobilization if provided, otherwise use internal
  const mobilization = externalMobilization || internalMobilization;
  const setMobilization = setExternalMobilization || setInternalMobilization;

  // Settings
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('estimateSettings');
    return saved ? JSON.parse(saved) : {
      wasteFactor: 5,
      contingency: 5,
      taxRate: 0,
      markup: 15,
      hideTaxes: false,
      hideWaste: false,
      hideContingency: false,
      hideMarkup: false,
      hideProfitMargin: false
    };
  });

  // Labor items with dropdown selection
  const [laborItems, setLaborItems] = useState(() => {
    const saved = localStorage.getItem('estimateLaborItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Equipment items with dropdown selection
  const [equipmentItems, setEquipmentItems] = useState(() => {
    const saved = localStorage.getItem('estimateEquipmentItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Other costs
  const [otherCosts, setOtherCosts] = useState(() => {
    const saved = localStorage.getItem('estimateOtherCosts');
    return saved ? JSON.parse(saved) : [];
  });

  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Template preview dialog
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  // Auto-save state
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(() => {
    const saved = localStorage.getItem('estimateAutoSaveEnabled');
    return saved ? JSON.parse(saved) : true; // Enabled by default
  });
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const autoSaveTimerRef = useRef(null);
  const AUTO_SAVE_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Sections expanded state
  const [expandedSections, setExpandedSections] = useState({
    calculators: true,
    calculatorLE: false,
    mobilization: true,
    schedule: true,
    labor: true,
    equipment: true,
    other: false,
    settings: false
  });

  // Labor types from database
  const laborTypes = defaultLaborTypes;
  const equipmentTypes = defaultEquipmentTypes;

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('estimateProjectInfo', JSON.stringify(projectInfo));
  }, [projectInfo]);

  useEffect(() => {
    localStorage.setItem('estimateCompanyInfo', JSON.stringify(companyInfo));
  }, [companyInfo]);

  useEffect(() => {
    localStorage.setItem('estimateTemplate', selectedTemplate);
  }, [selectedTemplate]);

  useEffect(() => {
    localStorage.setItem('estimateMobilization', JSON.stringify(internalMobilization));
  }, [internalMobilization]);

  useEffect(() => {
    localStorage.setItem('estimateSettings', JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    localStorage.setItem('estimateLaborItems', JSON.stringify(laborItems));
  }, [laborItems]);

  useEffect(() => {
    localStorage.setItem('estimateEquipmentItems', JSON.stringify(equipmentItems));
  }, [equipmentItems]);

  useEffect(() => {
    localStorage.setItem('estimateOtherCosts', JSON.stringify(otherCosts));
  }, [otherCosts]);

  // Save auto-save preference
  useEffect(() => {
    localStorage.setItem('estimateAutoSaveEnabled', JSON.stringify(autoSaveEnabled));
  }, [autoSaveEnabled]);

  // Auto-save function
  const performAutoSave = useCallback(() => {
    if (!autoSaveEnabled) return;
    
    try {
      setAutoSaveStatus('saving');
      
      // Get current estimate data
      const estimateData = {
        projectInfo,
        laborItems,
        equipmentItems,
        otherCosts,
        settings,
        savedAt: new Date().toISOString()
      };
      
      // Create auto-save entry
      const autoSaveName = projectInfo.name 
        ? `AutoSave - ${projectInfo.name}` 
        : `AutoSave - ${new Date().toLocaleDateString()}`;
      
      // Get existing local estimates
      const existingEstimates = JSON.parse(localStorage.getItem('massMasterPro_savedEstimates') || '[]');
      
      // Find and update or create auto-save entry
      const autoSaveIndex = existingEstimates.findIndex(e => e.name.startsWith('AutoSave -'));
      
      const autoSaveEntry = {
        estimate_id: autoSaveIndex >= 0 ? existingEstimates[autoSaveIndex].estimate_id : `autosave_${Date.now()}`,
        name: autoSaveName,
        description: 'Automatically saved draft',
        grand_total: 0, // Will be calculated
        estimate_data: estimateData,
        created_at: autoSaveIndex >= 0 ? existingEstimates[autoSaveIndex].created_at : new Date().toISOString(),
        updated_at: new Date().toISOString(),
        isAutoSave: true
      };
      
      if (autoSaveIndex >= 0) {
        existingEstimates[autoSaveIndex] = autoSaveEntry;
      } else {
        existingEstimates.unshift(autoSaveEntry);
      }
      
      // Keep only last 50 estimates
      const trimmedEstimates = existingEstimates.slice(0, 50);
      localStorage.setItem('massMasterPro_savedEstimates', JSON.stringify(trimmedEstimates));
      
      setLastAutoSave(new Date());
      setAutoSaveStatus('saved');
      
      // Reset status after 3 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
      
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    }
  }, [autoSaveEnabled, projectInfo, laborItems, equipmentItems, otherCosts, settings]);

  // Set up auto-save interval
  useEffect(() => {
    if (!autoSaveEnabled) {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }
    
    // Initial auto-save after 1 minute
    const initialTimer = setTimeout(() => {
      performAutoSave();
    }, 60 * 1000);
    
    // Then every 5 minutes
    autoSaveTimerRef.current = setInterval(() => {
      performAutoSave();
    }, AUTO_SAVE_INTERVAL);
    
    return () => {
      clearTimeout(initialTimer);
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [autoSaveEnabled, performAutoSave]);

  // Calculator totals - each calculator gets its own total
  const calculatorTotals = useMemo(() => {
    // Calculate civil total
    const civilTotal = (civilItems || []).reduce((sum, item) => sum + (parseFloat(item.totalCost) || 0), 0);
    
    // Calculate environmental total
    const envTotal = (environmentalItems || []).reduce((sum, item) => sum + (parseFloat(item.totalCost) || 0), 0);

    // Calculate backfill total
    const backfillTotal = (backfillItems || []).reduce((sum, item) => sum + (parseFloat(item.totalCost) || 0), 0);

    // Calculate trucking and disposal from trucking calculations
    let truckingTotal = 0;
    let disposalTotal = 0;
    (truckingCalculations || []).forEach(calc => {
      truckingTotal += (parseFloat(calc.totalHaulingCost) || 0);
      disposalTotal += (parseFloat(calc.totalDisposalCost) || 0);
    });

    return {
      demo: (areaTotals.totalCost || 0) + (pipingTotals?.totalCost || 0) + (tanksTotals?.totalCost || 0) + (buildingsTotals?.totalCost || 0),
      excavation: (excavationTotals.totalCost || 0) + (customExcavationTotals?.totalCost || 0),
      trucking: truckingTotal,
      disposal: disposalTotal,
      import: (bulkTotals.totalCost || 0) + (customImportTotals?.totalCost || 0),
      backfill: backfillTotal,
      civil: civilTotal,
      environmental: envTotal,
    };
  }, [areaTotals, bulkTotals, excavationTotals, backfillItems, pipingTotals, tanksTotals, buildingsTotals, truckingCalculations, civilItems, environmentalItems, customImportTotals, customExcavationTotals]);

  // Calculate labor & equipment from calculators
  const calculatorLETotal = useMemo(() => {
    let laborTotal = 0;
    let equipmentTotal = 0;
    
    Object.values(calculatorLaborEquipment || {}).forEach(section => {
      if (section.labor) {
        laborTotal += section.labor.reduce((sum, item) => sum + (parseFloat(item.totalCost) || 0), 0);
      }
      if (section.equipment) {
        equipmentTotal += section.equipment.reduce((sum, item) => sum + (parseFloat(item.totalCost) || 0), 0);
      }
    });
    
    return { labor: laborTotal, equipment: equipmentTotal, total: laborTotal + equipmentTotal };
  }, [calculatorLaborEquipment]);

  // Calculate mobilization totals from new mobilizationData structure (separate mob/demob)
  const mobilizationTotals = useMemo(() => {
    // Mobilization totals
    const mobEquipTotal = (mobilizationData?.mobilization?.equipment || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
    const mobLaborTotal = (mobilizationData?.mobilization?.labor || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
    const mobOtherTotal = (mobilizationData?.mobilization?.other || []).reduce((sum, item) => sum + (item.cost || 0), 0);
    const mobTotal = mobEquipTotal + mobLaborTotal + mobOtherTotal;

    // Demobilization totals
    const demobEquipTotal = (mobilizationData?.demobilization?.equipment || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
    const demobLaborTotal = (mobilizationData?.demobilization?.labor || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
    const demobOtherTotal = (mobilizationData?.demobilization?.other || []).reduce((sum, item) => sum + (item.cost || 0), 0);
    const demobTotal = demobEquipTotal + demobLaborTotal + demobOtherTotal;

    return {
      mobilization: {
        equipment: mobEquipTotal,
        labor: mobLaborTotal,
        other: mobOtherTotal,
        total: mobTotal
      },
      demobilization: {
        equipment: demobEquipTotal,
        labor: demobLaborTotal,
        other: demobOtherTotal,
        total: demobTotal
      },
      total: mobTotal + demobTotal
    };
  }, [mobilizationData]);

  // Total mobilization cost (for grand total calculation)
  const mobilizationTotal = useMemo(() => {
    return mobilizationTotals.total;
  }, [mobilizationTotals]);

  // Calculate labor total (estimate-specific)
  const laborTotal = useMemo(() => {
    return laborItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  }, [laborItems]);

  // Calculate equipment total (estimate-specific)
  const equipmentTotal = useMemo(() => {
    return equipmentItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  }, [equipmentItems]);

  // Calculate other costs total
  const otherTotal = useMemo(() => {
    return otherCosts.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  }, [otherCosts]);

  // Grand totals calculation (includes calculator L&E and mobilization)
  const grandTotals = useMemo(() => {
    const materialsSubtotal = Object.values(calculatorTotals).reduce((sum, val) => sum + val, 0);
    const calculatorLESubtotal = calculatorLETotal.total;
    const subtotal = materialsSubtotal + calculatorLESubtotal + mobilizationTotal + otherTotal;
    
    const wasteAmount = subtotal * (settings.wasteFactor / 100);
    const afterWaste = subtotal + wasteAmount;
    
    const contingencyAmount = afterWaste * (settings.contingency / 100);
    const afterContingency = afterWaste + contingencyAmount;
    
    // Tax is always calculated, but display is controlled by hideTaxes
    const taxAmount = afterContingency * (settings.taxRate / 100);
    const afterTax = afterContingency + taxAmount;
    
    const markupAmount = afterTax * (settings.markup / 100);
    const grandTotal = afterTax + markupAmount;
    
    const profitMargin = subtotal > 0 ? ((grandTotal - subtotal) / subtotal) * 100 : 0;

    return {
      materialsSubtotal,
      calculatorLESubtotal,
      mobilizationTotal,
      otherTotal,
      subtotal,
      wasteAmount,
      contingencyAmount,
      taxAmount,
      markupAmount,
      grandTotal,
      profitMargin
    };
  }, [calculatorTotals, calculatorLETotal, mobilizationTotal, otherTotal, settings]);

  // Add labor item
  const addLaborItem = () => {
    setLaborItems([...laborItems, {
      id: Date.now(),
      laborTypeId: '',
      laborName: '',
      workers: 1,
      hoursPerDay: 8,
      duration: 1,
      durationType: 'days',
      rate: 0,
      total: 0,
      // Per-item adjustments
      itemMarkup: 0,
      itemContingency: 0,
      itemWaste: 0
    }]);
  };

  // Update labor item
  const updateLaborItem = (id, field, value) => {
    setLaborItems(laborItems.map(item => {
      if (item.id !== id) return item;
      
      const updated = { ...item, [field]: value };
      
      // If selecting a labor type from dropdown - only set name, NOT rate
      if (field === 'laborTypeId' && value) {
        const laborType = laborTypes.find(lt => lt.id === parseInt(value));
        if (laborType) {
          updated.laborName = laborType.name;
          // Rate is manually entered - don't auto-fill
        }
      }
      
      // Calculate total based on duration type
      const workers = parseFloat(updated.workers) || 0;
      const hoursPerDay = parseFloat(updated.hoursPerDay) || 8;
      const duration = parseFloat(updated.duration) || 0;
      const rate = parseFloat(updated.rate) || 0;
      
      let totalHours = 0;
      switch (updated.durationType) {
        case 'hours':
          totalHours = duration;
          break;
        case 'days':
          totalHours = duration * hoursPerDay;
          break;
        case 'weeks':
          totalHours = duration * 5 * hoursPerDay;
          break;
        case 'months':
          totalHours = duration * 20 * hoursPerDay;
          break;
        default:
          totalHours = duration * hoursPerDay;
      }
      
      updated.total = workers * totalHours * rate;
      
      return updated;
    }));
  };

  // Remove labor item
  const removeLaborItem = (id) => {
    setLaborItems(laborItems.filter(item => item.id !== id));
  };

  // Add equipment item
  const addEquipmentItem = () => {
    setEquipmentItems([...equipmentItems, {
      id: Date.now(),
      equipmentTypeId: '',
      equipmentName: '',
      quantity: 1,
      duration: 1,
      durationType: 'days',
      rate: 0,
      total: 0,
      // Fuel
      fuelConsumption: 0,
      fuelCostPerGal: 0,
      fuelTotal: 0,
      // Per-item adjustments
      itemMarkup: 0,
      itemContingency: 0,
      itemWaste: 0,
      itemTax: 0
    }]);
  };

  // Update equipment item
  const updateEquipmentItem = (id, field, value) => {
    setEquipmentItems(equipmentItems.map(item => {
      if (item.id !== id) return item;
      
      const updated = { ...item, [field]: value };
      
      // If selecting equipment type from dropdown - only set name, NOT rate
      if (field === 'equipmentTypeId' && value) {
        const equipType = equipmentTypes.find(et => et.id === parseInt(value));
        if (equipType) {
          updated.equipmentName = equipType.name;
          // Rate is manually entered - don't auto-fill
        }
      }
      
      // Calculate total with fuel
      const quantity = parseFloat(updated.quantity) || 0;
      const duration = parseFloat(updated.duration) || 0;
      const rate = parseFloat(updated.rate) || 0;
      const fuelConsumption = parseFloat(updated.fuelConsumption) || 0;
      const fuelCostPerGal = parseFloat(updated.fuelCostPerGal) || 0;
      
      // Convert duration to hours for fuel calculation
      let totalHours = 0;
      switch (updated.durationType) {
        case 'hours': totalHours = duration; break;
        case 'days': totalHours = duration * 8; break;
        case 'weeks': totalHours = duration * 5 * 8; break;
        case 'months': totalHours = duration * 20 * 8; break;
        default: totalHours = duration * 8;
      }
      
      const equipmentCost = quantity * duration * rate;
      const fuelTotal = quantity * totalHours * fuelConsumption * fuelCostPerGal;
      updated.fuelTotal = fuelTotal;
      updated.total = equipmentCost + fuelTotal;
      
      return updated;
    }));
  };

  // Remove equipment item
  const removeEquipmentItem = (id) => {
    setEquipmentItems(equipmentItems.filter(item => item.id !== id));
  };

  // Add other cost
  const addOtherCost = () => {
    setOtherCosts([...otherCosts, {
      id: Date.now(),
      description: '',
      amount: 0
    }]);
  };

  // Update other cost
  const updateOtherCost = (id, field, value) => {
    setOtherCosts(otherCosts.map(item => {
      if (item.id !== id) return item;
      return { ...item, [field]: value };
    }));
  };

  // Remove other cost
  const removeOtherCost = (id) => {
    setOtherCosts(otherCosts.filter(item => item.id !== id));
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Prepare estimate data for export
  const getEstimateData = useCallback(() => {
    return {
      projectInfo,
      calculatorTotals,
      laborItems,
      equipmentItems,
      otherCosts,
      settings,
      grandTotals,
      materials: areaMaterials,
      bulkMaterials,
      excavationMaterials,
      civilItems,
      environmentalItems
    };
  }, [projectInfo, calculatorTotals, laborItems, equipmentItems, otherCosts, settings, grandTotals, areaMaterials, bulkMaterials, excavationMaterials, civilItems, environmentalItems]);

  // Load estimate data from saved estimates
  const loadEstimateData = useCallback((data) => {
    if (!data) return;
    
    // Load project info
    if (data.projectInfo) {
      setProjectInfo(prev => ({
        ...prev,
        ...data.projectInfo,
        date: data.projectInfo.date || new Date().toISOString().split('T')[0]
      }));
    }
    
    // Load settings
    if (data.settings) {
      setSettings(prev => ({ ...prev, ...data.settings }));
    }
    
    // Load labor items
    if (data.laborItems) {
      setLaborItems(data.laborItems);
    }
    
    // Load equipment items
    if (data.equipmentItems) {
      setEquipmentItems(data.equipmentItems);
    }
    
    // Load other costs
    if (data.otherCosts) {
      setOtherCosts(data.otherCosts);
    }
    
    toast.success('Estimate loaded successfully');
  }, []);

  // Handle download
  const handleDownload = async (format) => {
    try {
      const estimateData = getEstimateData();
      
      if (format === 'pdf') {
        await downloadPDFReport(estimateData, projectInfo.name || 'Estimate');
        toast.success('PDF downloaded successfully');
      } else if (format === 'word') {
        await downloadWordDocument(estimateData, projectInfo.name || 'Estimate');
        toast.success('Word document downloaded successfully');
      } else if (format === 'excel') {
        await downloadComprehensiveExcel(estimateData, projectInfo.name || 'Estimate');
        toast.success('Excel spreadsheet downloaded successfully');
      } else if (format === 'clipboard') {
        await copyReportToClipboard(estimateData);
        toast.success('Estimate copied to clipboard');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download: ${error.message}`);
    }
  };

  // Handle email send
  const handleSendEmail = async () => {
    if (!recipientEmail) {
      toast.error('Please enter a recipient email');
      return;
    }

    setIsSending(true);
    try {
      const estimateData = getEstimateData();
      // Pass selected template to getPDFBase64 for template-styled PDF
      const pdfBase64 = await getPDFBase64(estimateData, projectInfo.name || 'Estimate', selectedTemplate);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/send-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recipientEmail,
          subject: `Project Estimate: ${projectInfo.name || 'Construction Estimate'}`,
          report: `Project Estimate from Mass Master Pro\n\nProject: ${projectInfo.name}\nTotal: ${formatCurrency(grandTotals.grandTotal)}`,
          attachments: [{
            filename: `${projectInfo.name || 'Estimate'}.pdf`,
            content: pdfBase64,
            type: 'application/pdf'
          }]
        })
      });

      if (response.ok) {
        toast.success(`Estimate sent to ${recipientEmail}`);
        setShowEmailDialog(false);
        setRecipientEmail('');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Email error:', error);
      toast.error(`Failed to send email: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Calculator row component
  const CalculatorRow = ({ icon: Icon, name, amount, color }) => (
    <div className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-lg">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-sm font-medium">{name}</span>
      </div>
      <span className={`font-semibold ${amount > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
        {formatCurrency(amount)}
      </span>
    </div>
  );

  return (
    <div className="space-y-6 p-4 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Project Estimate
          </h1>
          <p className="text-muted-foreground text-sm">Create professional construction estimates</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <SavedEstimates 
            getEstimateData={getEstimateData}
            loadEstimateData={loadEstimateData}
            currentProjectName={projectInfo.name}
          />
          <Button variant="outline" onClick={() => handleDownload('pdf')} data-testid="save-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            Save PDF
          </Button>
          <Button variant="outline" onClick={() => handleDownload('excel')} data-testid="save-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Save Excel
          </Button>
          <Button onClick={() => setShowEmailDialog(true)} data-testid="email-btn">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
        </div>
      </div>

      {/* Template Selection - At Top */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Layout className="w-4 h-4" />
              Estimate Template
            </h3>
            <Button variant="outline" size="sm" onClick={() => setShowTemplatePreview(true)}>
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {ESTIMATE_TEMPLATES.map((template) => {
              const Icon = template.icon;
              const isSelected = selectedTemplate === template.id;
              return (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  data-testid={`template-${template.id}`}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    isSelected 
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/20' 
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                  <p className="text-xs font-medium">{template.name}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Auto-save indicator */}
      <div className="flex items-center justify-between bg-muted/30 rounded-lg px-4 py-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="autoSaveToggle"
              checked={autoSaveEnabled}
              onCheckedChange={(checked) => setAutoSaveEnabled(checked === true)}
              data-testid="auto-save-toggle"
            />
            <Label htmlFor="autoSaveToggle" className="text-sm cursor-pointer">
              Auto-save every 5 minutes
            </Label>
          </div>
          {autoSaveEnabled && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {autoSaveStatus === 'saving' && (
                <>
                  <Clock className="w-3 h-3 animate-spin" />
                  <span>Saving...</span>
                </>
              )}
              {autoSaveStatus === 'saved' && (
                <>
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span className="text-green-600">Draft saved</span>
                </>
              )}
              {autoSaveStatus === 'error' && (
                <>
                  <AlertCircle className="w-3 h-3 text-red-500" />
                  <span className="text-red-600">Save failed</span>
                </>
              )}
              {autoSaveStatus === 'idle' && lastAutoSave && (
                <>
                  <Clock className="w-3 h-3" />
                  <span>Last saved: {lastAutoSave.toLocaleTimeString()}</span>
                </>
              )}
            </div>
          )}
        </div>
        {autoSaveEnabled && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={performAutoSave}
            className="text-xs h-7"
            data-testid="save-now-btn"
          >
            <Save className="w-3 h-3 mr-1" />
            Save Now
          </Button>
        )}
      </div>

      {/* Project Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Project Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Project Name</Label>
              <Input
                placeholder="Enter project name"
                value={projectInfo.name}
                onChange={(e) => setProjectInfo({ ...projectInfo, name: e.target.value })}
                data-testid="project-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Project Number</Label>
              <Input
                placeholder="e.g., PRJ-2025-001"
                value={projectInfo.number}
                onChange={(e) => setProjectInfo({ ...projectInfo, number: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={projectInfo.date}
                onChange={(e) => setProjectInfo({ ...projectInfo, date: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Location</Label>
              <Input
                placeholder="Project location"
                value={projectInfo.location}
                onChange={(e) => setProjectInfo({ ...projectInfo, location: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Client Name</Label>
              <Input
                placeholder="Client name"
                value={projectInfo.client}
                onChange={(e) => setProjectInfo({ ...projectInfo, client: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Client Email</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={projectInfo.clientEmail}
                onChange={(e) => setProjectInfo({ ...projectInfo, clientEmail: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Info (Your Business Details) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Your Company Information
          </CardTitle>
          <p className="text-xs text-muted-foreground">This information will appear at the top of your estimate</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Company Name</Label>
              <Input
                placeholder="Your Company Name"
                value={companyInfo.name}
                onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Address</Label>
              <Input
                placeholder="123 Main Street"
                value={companyInfo.address}
                onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">City, State ZIP</Label>
              <Input
                placeholder="City, ST 12345"
                value={companyInfo.city}
                onChange={(e) => setCompanyInfo({ ...companyInfo, city: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Phone</Label>
              <Input
                placeholder="(555) 123-4567"
                value={companyInfo.phone}
                onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                placeholder="info@yourcompany.com"
                value={companyInfo.email}
                onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Website</Label>
              <Input
                placeholder="www.yourcompany.com"
                value={companyInfo.website}
                onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })}
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">License / Registration #</Label>
              <Input
                placeholder="Contractor License #123456"
                value={companyInfo.license}
                onChange={(e) => setCompanyInfo({ ...companyInfo, license: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculator Totals Section - With L&E Inline */}
      <Collapsible open={expandedSections.calculators} onOpenChange={() => toggleSection('calculators')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Calculator Costs
                  <Badge variant="secondary" className="ml-2">
                    {formatCurrency(grandTotals.materialsSubtotal + grandTotals.calculatorLESubtotal + mobilizationTotal)}
                  </Badge>
                </CardTitle>
                {expandedSections.calculators ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-3">
              {/* Mobilization Section */}
              {mobilizationTotals.mobilization.total > 0 && (
                <div className="space-y-2 pb-3 border-b border-border">
                  <div className="flex items-center justify-between py-2 px-3 bg-orange-500/10 rounded-lg">
                    <span className="font-semibold flex items-center gap-2">
                      <Truck className="w-4 h-4 text-orange-500" />
                      Mobilization
                    </span>
                    <span className="font-bold text-orange-600">{formatCurrency(mobilizationTotals.mobilization.total)}</span>
                  </div>
                  <div className="ml-6 pl-4 border-l-2 border-orange-200 space-y-1">
                    {(mobilizationData?.mobilization?.equipment || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <HardHat className="w-3 h-3 text-orange-400" />
                          Equipment - {item.name}
                          {item.fuelTotalCost > 0 && <Badge variant="outline" className="text-[10px] ml-1 text-green-600">+Fuel</Badge>}
                        </span>
                        <span className="text-orange-600">{formatCurrency(item.totalCost)}</span>
                      </div>
                    ))}
                    {(mobilizationData?.mobilization?.labor || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Users className="w-3 h-3 text-orange-400" />
                          Labor - {item.name}
                        </span>
                        <span className="text-orange-600">{formatCurrency(item.totalCost)}</span>
                      </div>
                    ))}
                    {(mobilizationData?.mobilization?.other || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <FileText className="w-3 h-3 text-orange-400" />
                          {item.description}
                        </span>
                        <span className="text-orange-600">{formatCurrency(item.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Demobilization Section */}
              {mobilizationTotals.demobilization.total > 0 && (
                <div className="space-y-2 pb-3 border-b border-border">
                  <div className="flex items-center justify-between py-2 px-3 bg-blue-500/10 rounded-lg">
                    <span className="font-semibold flex items-center gap-2">
                      <Truck className="w-4 h-4 text-blue-500 scale-x-[-1]" />
                      Demobilization
                    </span>
                    <span className="font-bold text-blue-600">{formatCurrency(mobilizationTotals.demobilization.total)}</span>
                  </div>
                  <div className="ml-6 pl-4 border-l-2 border-blue-200 space-y-1">
                    {(mobilizationData?.demobilization?.equipment || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <HardHat className="w-3 h-3 text-blue-400" />
                          Equipment - {item.name}
                          {item.fuelTotalCost > 0 && <Badge variant="outline" className="text-[10px] ml-1 text-green-600">+Fuel</Badge>}
                        </span>
                        <span className="text-blue-600">{formatCurrency(item.totalCost)}</span>
                      </div>
                    ))}
                    {(mobilizationData?.demobilization?.labor || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Users className="w-3 h-3 text-blue-400" />
                          Labor - {item.name}
                        </span>
                        <span className="text-blue-600">{formatCurrency(item.totalCost)}</span>
                      </div>
                    ))}
                    {(mobilizationData?.demobilization?.other || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <FileText className="w-3 h-3 text-blue-400" />
                          {item.description}
                        </span>
                        <span className="text-blue-600">{formatCurrency(item.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Demo Section */}
              {(calculatorTotals.demo > 0 || (calculatorLaborEquipment?.demo?.labor?.length > 0) || (calculatorLaborEquipment?.demo?.equipment?.length > 0)) && (
                <div className="space-y-2">
                  <CalculatorRow icon={Construction} name="Demolition Materials" amount={calculatorTotals.demo} color="text-blue-500" />
                  {((calculatorLaborEquipment?.demo?.labor?.length > 0) || (calculatorLaborEquipment?.demo?.equipment?.length > 0)) && (
                    <div className="ml-6 pl-4 border-l-2 border-blue-200 space-y-1">
                      {(calculatorLaborEquipment?.demo?.labor || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3 text-blue-400" />
                            L&E Demolition - {item.name}
                          </span>
                          <span className="text-blue-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                      {(calculatorLaborEquipment?.demo?.equipment || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <HardHat className="w-3 h-3 text-blue-400" />
                            L&E Demo - {item.name}
                          </span>
                          <span className="text-blue-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Excavation Section */}
              {(calculatorTotals.excavation > 0 || (calculatorLaborEquipment?.excavation?.labor?.length > 0) || (calculatorLaborEquipment?.excavation?.equipment?.length > 0)) && (
                <div className="space-y-2">
                  <CalculatorRow icon={Shovel} name="Excavation" amount={calculatorTotals.excavation} color="text-amber-500" />
                  {((calculatorLaborEquipment?.excavation?.labor?.length > 0) || (calculatorLaborEquipment?.excavation?.equipment?.length > 0)) && (
                    <div className="ml-6 pl-4 border-l-2 border-amber-200 space-y-1">
                      {(calculatorLaborEquipment?.excavation?.labor || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3 text-amber-400" />
                            L&E Excavation - {item.name}
                          </span>
                          <span className="text-amber-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                      {(calculatorLaborEquipment?.excavation?.equipment || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <HardHat className="w-3 h-3 text-amber-400" />
                            L&E Excavation - {item.name}
                          </span>
                          <span className="text-amber-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Trucking & Disposal */}
              <CalculatorRow icon={Truck} name="Trucking (T&D)" amount={calculatorTotals.trucking} color="text-orange-500" />
              <CalculatorRow icon={Trash2} name="Disposal Fees" amount={calculatorTotals.disposal} color="text-red-500" />
              
              {/* Import Section - No L&E */}
              {calculatorTotals.import > 0 && (
                <CalculatorRow icon={Package} name="Import Fill" amount={calculatorTotals.import} color="text-yellow-500" />
              )}
              
              {/* Backfill Section */}
              {(calculatorTotals.backfill > 0 || (calculatorLaborEquipment?.backfill?.labor?.length > 0) || (calculatorLaborEquipment?.backfill?.equipment?.length > 0)) && (
                <div className="space-y-2">
                  <CalculatorRow icon={Layers} name="Backfill" amount={calculatorTotals.backfill} color="text-teal-500" />
                  {((calculatorLaborEquipment?.backfill?.labor?.length > 0) || (calculatorLaborEquipment?.backfill?.equipment?.length > 0)) && (
                    <div className="ml-6 pl-4 border-l-2 border-teal-200 space-y-1">
                      {(calculatorLaborEquipment?.backfill?.labor || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3 text-teal-400" />
                            L&E Backfill - {item.name}
                          </span>
                          <span className="text-teal-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                      {(calculatorLaborEquipment?.backfill?.equipment || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <HardHat className="w-3 h-3 text-teal-400" />
                            L&E Backfill - {item.name}
                          </span>
                          <span className="text-teal-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Civil Section */}
              {(calculatorTotals.civil > 0 || (calculatorLaborEquipment?.civil?.labor?.length > 0) || (calculatorLaborEquipment?.civil?.equipment?.length > 0)) && (
                <div className="space-y-2">
                  <CalculatorRow icon={Building2} name="Civil Work" amount={calculatorTotals.civil} color="text-slate-500" />
                  {((calculatorLaborEquipment?.civil?.labor?.length > 0) || (calculatorLaborEquipment?.civil?.equipment?.length > 0)) && (
                    <div className="ml-6 pl-4 border-l-2 border-slate-200 space-y-1">
                      {(calculatorLaborEquipment?.civil?.labor || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3 text-slate-400" />
                            L&E Civil - {item.name}
                          </span>
                          <span className="text-slate-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                      {(calculatorLaborEquipment?.civil?.equipment || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <HardHat className="w-3 h-3 text-slate-400" />
                            L&E Civil - {item.name}
                          </span>
                          <span className="text-slate-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Environmental Section */}
              {(calculatorTotals.environmental > 0 || (calculatorLaborEquipment?.environmental?.labor?.length > 0) || (calculatorLaborEquipment?.environmental?.equipment?.length > 0)) && (
                <div className="space-y-2">
                  <CalculatorRow icon={Leaf} name="Environmental" amount={calculatorTotals.environmental} color="text-green-500" />
                  {((calculatorLaborEquipment?.environmental?.labor?.length > 0) || (calculatorLaborEquipment?.environmental?.equipment?.length > 0)) && (
                    <div className="ml-6 pl-4 border-l-2 border-green-200 space-y-1">
                      {(calculatorLaborEquipment?.environmental?.labor || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3 text-green-400" />
                            L&E Environmental - {item.name}
                          </span>
                          <span className="text-green-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                      {(calculatorLaborEquipment?.environmental?.equipment || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <HardHat className="w-3 h-3 text-green-400" />
                            L&E Environmental - {item.name}
                          </span>
                          <span className="text-green-600">{formatCurrency(item.totalCost)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <Separator className="my-2" />
              <div className="flex items-center justify-between py-2 px-3 bg-primary/10 rounded-lg">
                <span className="font-semibold">Calculator Total (Materials + L&E)</span>
                <span className="font-bold text-primary">{formatCurrency(grandTotals.materialsSubtotal + grandTotals.calculatorLESubtotal)}</span>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Project Schedule / Task Durations */}
      {taskDurations && Object.values(taskDurations).some(d => d?.value) && (
        <Collapsible open={expandedSections.schedule} onOpenChange={() => toggleSection('schedule')}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-500" />
                    Project Schedule
                    <Badge variant="secondary" className="ml-2">
                      {Object.values(taskDurations).filter(d => d?.value).length} phases
                    </Badge>
                  </CardTitle>
                  {expandedSections.schedule ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {taskDurations.demo?.value && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm font-medium">Demolition</span>
                      <Badge variant="outline">{taskDurations.demo.value} {taskDurations.demo.unit}</Badge>
                    </div>
                  )}
                  {taskDurations.excavation?.value && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm font-medium">Excavation</span>
                      <Badge variant="outline">{taskDurations.excavation.value} {taskDurations.excavation.unit}</Badge>
                    </div>
                  )}
                  {taskDurations.backfill?.value && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm font-medium">Backfill</span>
                      <Badge variant="outline">{taskDurations.backfill.value} {taskDurations.backfill.unit}</Badge>
                    </div>
                  )}
                  {taskDurations.civil?.value && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm font-medium">Civil Work</span>
                      <Badge variant="outline">{taskDurations.civil.value} {taskDurations.civil.unit}</Badge>
                    </div>
                  )}
                  {taskDurations.environmental?.value && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm font-medium">Environmental</span>
                      <Badge variant="outline">{taskDurations.environmental.value} {taskDurations.environmental.unit}</Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Other Costs Section */}
      <Collapsible open={expandedSections.other} onOpenChange={() => toggleSection('other')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Other Costs
                  <Badge variant="secondary" className="ml-2">
                    {formatCurrency(otherTotal)}
                  </Badge>
                </CardTitle>
                {expandedSections.other ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-3">
              {otherCosts.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  <Input
                    placeholder="Description (e.g., Permits, Insurance)"
                    value={item.description}
                    onChange={(e) => updateOtherCost(item.id, 'description', e.target.value)}
                    className="flex-1"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.amount}
                      onChange={(e) => updateOtherCost(item.id, 'amount', parseFloat(e.target.value) || 0)}
                      className="w-32"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeOtherCost(item.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addOtherCost} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Other Cost
              </Button>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Markup & Settings Section */}
      <Collapsible open={expandedSections.settings} onOpenChange={() => toggleSection('settings')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Percent className="w-5 h-5 text-purple-500" />
                  Markup & Settings
                </CardTitle>
                {expandedSections.settings ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Waste Factor %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={settings.wasteFactor}
                    onChange={(e) => setSettings({ ...settings, wasteFactor: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Contingency %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={settings.contingency}
                    onChange={(e) => setSettings({ ...settings, contingency: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                {settings.hideTaxes !== true && (
                  <div className="space-y-1">
                    <Label className="text-xs">Tax Rate %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="25"
                      value={settings.taxRate}
                      onChange={(e) => setSettings({ ...settings, taxRate: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Markup %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.markup}
                    onChange={(e) => setSettings({ ...settings, markup: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              
              {/* Hide Options */}
              <div className="mt-4 p-3 bg-muted/50 rounded-lg space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Display Options (hide from estimate but include in total)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hideWaste"
                      checked={settings.hideWaste === true}
                      onCheckedChange={(checked) => setSettings({ ...settings, hideWaste: checked === true })}
                      data-testid="hide-waste-checkbox"
                    />
                    <Label htmlFor="hideWaste" className="text-sm cursor-pointer">
                      Hide Waste
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hideContingency"
                      checked={settings.hideContingency === true}
                      onCheckedChange={(checked) => setSettings({ ...settings, hideContingency: checked === true })}
                      data-testid="hide-contingency-checkbox"
                    />
                    <Label htmlFor="hideContingency" className="text-sm cursor-pointer">
                      Hide Contingency
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hideTaxes"
                      checked={settings.hideTaxes === true}
                      onCheckedChange={(checked) => setSettings({ ...settings, hideTaxes: checked === true })}
                      data-testid="hide-taxes-checkbox"
                    />
                    <Label htmlFor="hideTaxes" className="text-sm cursor-pointer">
                      Hide Taxes
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hideMarkup"
                      checked={settings.hideMarkup === true}
                      onCheckedChange={(checked) => setSettings({ ...settings, hideMarkup: checked === true })}
                      data-testid="hide-markup-checkbox"
                    />
                    <Label htmlFor="hideMarkup" className="text-sm cursor-pointer">
                      Hide Markup
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hideProfitMargin"
                      checked={settings.hideProfitMargin === true}
                      onCheckedChange={(checked) => setSettings({ ...settings, hideProfitMargin: checked === true })}
                      data-testid="hide-profit-margin-checkbox"
                    />
                    <Label htmlFor="hideProfitMargin" className="text-sm cursor-pointer">
                      Hide Profit Margin
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Grand Total */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Estimate Summary</h3>
          <div className="space-y-3">
            {/* Calculator Breakdown */}
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Calculator Costs</div>
            {calculatorTotals.demo > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Demo Materials</span>
                <span>{formatCurrency(calculatorTotals.demo)}</span>
              </div>
            )}
            {calculatorTotals.excavation > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Excavation</span>
                <span>{formatCurrency(calculatorTotals.excavation)}</span>
              </div>
            )}
            {calculatorTotals.trucking > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Trucking (T&D)</span>
                <span>{formatCurrency(calculatorTotals.trucking)}</span>
              </div>
            )}
            {calculatorTotals.disposal > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Disposal Fees</span>
                <span>{formatCurrency(calculatorTotals.disposal)}</span>
              </div>
            )}
            {calculatorTotals.import > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Import Fill</span>
                <span>{formatCurrency(calculatorTotals.import)}</span>
              </div>
            )}
            {calculatorTotals.backfill > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Backfill</span>
                <span>{formatCurrency(calculatorTotals.backfill)}</span>
              </div>
            )}
            {calculatorTotals.civil > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Civil Work</span>
                <span>{formatCurrency(calculatorTotals.civil)}</span>
              </div>
            )}
            {calculatorTotals.environmental > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Environmental</span>
                <span>{formatCurrency(calculatorTotals.environmental)}</span>
              </div>
            )}
            
            {/* Calculator Labor & Equipment */}
            {grandTotals.calculatorLESubtotal > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Calculator L&E</span>
                <span>{formatCurrency(grandTotals.calculatorLESubtotal)}</span>
              </div>
            )}

            {/* Mobilization & Demobilization */}
            {grandTotals.mobilizationTotal > 0 && (
              <>
                <Separator className="my-2" />
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Mob/Demob</div>
                {mobilizationTotals.mobilization.total > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Mobilization</span>
                    <span>{formatCurrency(mobilizationTotals.mobilization.total)}</span>
                  </div>
                )}
                {mobilizationTotals.demobilization.total > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Demobilization</span>
                    <span>{formatCurrency(mobilizationTotals.demobilization.total)}</span>
                  </div>
                )}
              </>
            )}
            
            {/* Other Costs */}
            {grandTotals.otherTotal > 0 && (
              <>
                <Separator className="my-2" />
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Additional Costs</div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Other Costs</span>
                  <span>{formatCurrency(grandTotals.otherTotal)}</span>
                </div>
              </>
            )}
            
            <Separator className="my-3" />
            <div className="flex justify-between text-sm font-semibold bg-muted/50 p-2 rounded">
              <span>Subtotal</span>
              <span>{formatCurrency(grandTotals.subtotal)}</span>
            </div>
            
            {/* Adjustments - Show based on hide settings */}
            {((!settings.hideWaste && grandTotals.wasteAmount > 0) || 
              (!settings.hideContingency && grandTotals.contingencyAmount > 0) || 
              (!settings.hideMarkup && grandTotals.markupAmount > 0) || 
              (!settings.hideTaxes && grandTotals.taxAmount > 0)) && (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-3 mb-2">Adjustments</div>
            )}
            {!settings.hideWaste && grandTotals.wasteAmount > 0 && (
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Waste ({settings.wasteFactor}%)</span>
                <span>+ {formatCurrency(grandTotals.wasteAmount)}</span>
              </div>
            )}
            {!settings.hideContingency && grandTotals.contingencyAmount > 0 && (
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Contingency ({settings.contingency}%)</span>
                <span>+ {formatCurrency(grandTotals.contingencyAmount)}</span>
              </div>
            )}
            {!settings.hideTaxes && grandTotals.taxAmount > 0 && (
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Tax ({settings.taxRate}%)</span>
                <span>+ {formatCurrency(grandTotals.taxAmount)}</span>
              </div>
            )}
            {!settings.hideMarkup && grandTotals.markupAmount > 0 && (
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Markup ({settings.markup}%)</span>
                <span>+ {formatCurrency(grandTotals.markupAmount)}</span>
              </div>
            )}
            
            <Separator className="my-3" />
            <div className="flex justify-between items-center pt-2">
              <span className="text-xl font-bold">Grand Total</span>
              <span className="text-3xl font-bold text-primary" data-testid="grand-total">
                {formatCurrency(grandTotals.grandTotal)}
              </span>
            </div>
            {!settings.hideProfitMargin && grandTotals.profitMargin > 0 && (
              <div className="text-right text-sm text-muted-foreground">
                Profit Margin: {grandTotals.profitMargin.toFixed(1)}%
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Estimate via Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                data-testid="email-recipient-input"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              The estimate will be sent as a PDF attachment to the recipient.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={isSending} data-testid="send-email-btn">
              {isSending ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={showTemplatePreview} onOpenChange={setShowTemplatePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Template Preview: {ESTIMATE_TEMPLATES.find(t => t.id === selectedTemplate)?.name}
            </DialogTitle>
            <DialogDescription>
              Preview how your estimate will appear when exported
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            {/* Template Preview Content */}
            <div className={`p-6 ${
              selectedTemplate === 'professional' ? 'bg-white' :
              selectedTemplate === 'modern' ? 'bg-slate-800 text-slate-100' :
              selectedTemplate === 'classic' ? 'bg-amber-50' :
              selectedTemplate === 'minimal' ? 'bg-white' :
              selectedTemplate === 'excel' ? 'bg-white font-mono' :
              'bg-orange-50'
            }`}>
              {/* Company Header */}
              <div className={`mb-6 pb-4 border-b-2 ${
                selectedTemplate === 'professional' ? 'border-blue-600' :
                selectedTemplate === 'modern' ? 'border-slate-500' :
                selectedTemplate === 'classic' ? 'border-gray-800' :
                selectedTemplate === 'minimal' ? 'border-gray-200' :
                selectedTemplate === 'excel' ? 'border-green-600' :
                'border-orange-500'
              }`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className={`text-2xl font-bold ${
                      selectedTemplate === 'professional' ? 'text-blue-800' :
                      selectedTemplate === 'modern' ? 'text-slate-100' :
                      selectedTemplate === 'classic' ? 'text-gray-900' :
                      selectedTemplate === 'minimal' ? 'text-gray-800' :
                      selectedTemplate === 'excel' ? 'text-green-800' :
                      'text-orange-800'
                    }`}>
                      {companyInfo.name || 'Your Company Name'}
                    </h1>
                    <p className={`text-sm ${selectedTemplate === 'modern' ? 'text-slate-300' : 'text-gray-600'}`}>{companyInfo.address || '123 Main Street'}</p>
                    <p className={`text-sm ${selectedTemplate === 'modern' ? 'text-slate-300' : 'text-gray-600'}`}>{companyInfo.city || 'City, ST 12345'}</p>
                    <div className={`flex gap-4 mt-2 text-xs ${selectedTemplate === 'modern' ? 'text-slate-400' : 'text-gray-500'}`}>
                      {companyInfo.phone && <span>{companyInfo.phone}</span>}
                      {companyInfo.email && <span>{companyInfo.email}</span>}
                      {companyInfo.website && <span>{companyInfo.website}</span>}
                    </div>
                    {companyInfo.license && (
                      <p className={`text-xs mt-1 ${selectedTemplate === 'modern' ? 'text-slate-400' : 'text-gray-500'}`}>{companyInfo.license}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <h2 className={`text-xl font-bold ${
                      selectedTemplate === 'professional' ? 'text-blue-600' :
                      selectedTemplate === 'modern' ? 'text-cyan-400' :
                      selectedTemplate === 'classic' ? 'text-gray-700' :
                      selectedTemplate === 'minimal' ? 'text-gray-700' :
                      selectedTemplate === 'excel' ? 'text-green-700' :
                      'text-orange-600'
                    }`}>
                      ESTIMATE
                    </h2>
                    <p className="text-sm">#{projectInfo.number || 'EST-001'}</p>
                    <p className="text-sm">{projectInfo.date || new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Client & Project Info */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To</h3>
                  <p className="font-semibold">{projectInfo.client || 'Client Name'}</p>
                  <p className="text-sm text-gray-600">{projectInfo.clientEmail || 'client@email.com'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Project</h3>
                  <p className="font-semibold">{projectInfo.name || 'Project Name'}</p>
                  <p className="text-sm text-gray-600">{projectInfo.location || 'Project Location'}</p>
                </div>
              </div>

              {/* Sample Line Items */}
              <div className={`mb-6 ${selectedTemplate === 'excel' ? 'border border-gray-400' : ''}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`${
                      selectedTemplate === 'professional' ? 'bg-blue-600 text-white' :
                      selectedTemplate === 'modern' ? 'bg-slate-700 text-slate-100' :
                      selectedTemplate === 'classic' ? 'bg-gray-800 text-white' :
                      selectedTemplate === 'minimal' ? 'bg-gray-100 text-gray-800 border-b' :
                      selectedTemplate === 'excel' ? 'bg-green-100 text-green-900 border-b border-gray-400' :
                      'bg-orange-600 text-white'
                    }`}>
                      <th className="text-left p-2">Description</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Unit</th>
                      <th className="text-right p-2">Rate</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody className={selectedTemplate === 'modern' ? 'text-slate-200' : ''}>
                    <tr className={`${selectedTemplate === 'excel' ? 'border-b border-gray-300' : selectedTemplate === 'modern' ? 'border-b border-slate-600' : 'border-b'}`}>
                      <td className="p-2">Demo Materials</td>
                      <td className="text-right p-2">1</td>
                      <td className="text-right p-2">LS</td>
                      <td className="text-right p-2">{formatCurrency(calculatorTotals.demo || 5000)}</td>
                      <td className="text-right p-2">{formatCurrency(calculatorTotals.demo || 5000)}</td>
                    </tr>
                    <tr className={`${selectedTemplate === 'excel' ? 'border-b border-gray-300' : selectedTemplate === 'modern' ? 'border-b border-slate-600' : 'border-b'}`}>
                      <td className="p-2">Excavation</td>
                      <td className="text-right p-2">1</td>
                      <td className="text-right p-2">LS</td>
                      <td className="text-right p-2">{formatCurrency(calculatorTotals.excavation || 3500)}</td>
                      <td className="text-right p-2">{formatCurrency(calculatorTotals.excavation || 3500)}</td>
                    </tr>
                    <tr className={`${selectedTemplate === 'excel' ? 'border-b border-gray-300' : selectedTemplate === 'modern' ? 'border-b border-slate-600' : 'border-b'}`}>
                      <td className="p-2">Mobilization</td>
                      <td className="text-right p-2">1</td>
                      <td className="text-right p-2">LS</td>
                      <td className="text-right p-2">{formatCurrency(mobilization?.mobilizationCost || 1500)}</td>
                      <td className="text-right p-2">{formatCurrency(mobilization?.mobilizationCost || 1500)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64">
                  <div className={`flex justify-between py-1 border-b ${selectedTemplate === 'modern' ? 'border-slate-600 text-slate-200' : ''}`}>
                    <span>Subtotal</span>
                    <span>{formatCurrency(grandTotals.subtotal || 10000)}</span>
                  </div>
                  {settings.wasteFactor > 0 && (
                    <div className={`flex justify-between py-1 text-sm ${selectedTemplate === 'modern' ? 'text-slate-400' : 'text-gray-600'}`}>
                      <span>Waste ({settings.wasteFactor}%)</span>
                      <span>{formatCurrency(grandTotals.wasteAmount || 500)}</span>
                    </div>
                  )}
                  {settings.markup > 0 && (
                    <div className={`flex justify-between py-1 text-sm ${selectedTemplate === 'modern' ? 'text-slate-400' : 'text-gray-600'}`}>
                      <span>Markup ({settings.markup}%)</span>
                      <span>{formatCurrency(grandTotals.markupAmount || 1500)}</span>
                    </div>
                  )}
                  <div className={`flex justify-between py-2 mt-2 font-bold text-lg ${
                    selectedTemplate === 'professional' ? 'bg-blue-100 text-blue-800' :
                    selectedTemplate === 'modern' ? 'bg-slate-600 text-cyan-400' :
                    selectedTemplate === 'classic' ? 'bg-gray-200 text-gray-900' :
                    selectedTemplate === 'minimal' ? 'border-t-2 border-gray-800' :
                    selectedTemplate === 'excel' ? 'bg-green-100 text-green-800 border border-green-400' :
                    'bg-orange-100 text-orange-800'
                  } px-2 rounded`}>
                    <span>Total</span>
                    <span>{formatCurrency(grandTotals.grandTotal || 12000)}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-4 border-t text-center text-xs text-gray-400">
                <p>Created by MassMasterPro</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplatePreview(false)}>
              Close
            </Button>
            <Button onClick={() => { setShowTemplatePreview(false); handleDownload('pdf'); }}>
              <Download className="w-4 h-4 mr-2" />
              Download as PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstimateSpreadsheet;
